import React, { useMemo } from 'react';
import PropTypes from 'prop-types';
import { Modal, Button } from 'react-bootstrap';
import styledComponent from '../../../utils/styledComponent';
import theme from '../../../theme';
import { getStopRowKey } from './utils';

// Review modal for the batch consolidation flow. Surfaces what NextMV is
// about to do — which routes are being removed, how many vehicles remain,
// which stops will be redistributed — and submits a DriverCallOff reroute
// on confirm. Per the rerouting-scenarios PDF, DriverCallOff takes only
// previousRunId + missingVehicles; Nextmv handles redistribution by
// reseeding the previous run's stops with fixed=False, so the supervisor
// doesn't need to touch individual windows here. Per-stop window edits
// belong in the route editor's Pin flow after the reroute confirms.
//
// Two trigger sources:
//   - 'headcount' (the Headcount section's Consolidate Routes CTA — fired
//     after a driver/van shortage was logged)
//   - 'manual'    (the workspace-header "Consolidate Selected" button —
//     fires whenever ≥1 route is checked)

const StyledDiv = styledComponent('div', {
  // Cap the modal body so long redistribute lists don't push the footer
  // (Cancel / Send to NextMV) off-screen on shorter viewports. Footer is
  // anchored to the Modal.Footer; this scrolls the body inside it.
  maxHeight: '70vh',
  overflowY: 'auto',

  '& .csm-warning': {
    background: theme.colors.warningBg,
    borderLeft: `4px solid ${theme.colors.warning}`,
    padding: theme.spacing.bodyPadding,
    borderRadius: theme.radii.input,
    marginBottom: theme.spacing.sectionGap,
    fontSize: theme.typography.body.fontSize,
    color: theme.colors.textDark,
  },
  '& .csm-warning strong': {
    color: theme.colors.cautionText,
  },
  '& .csm-stats': {
    display: 'grid',
    gridTemplateColumns: 'repeat(3, 1fr)',
    gap: theme.spacing.fieldGap,
    marginBottom: theme.spacing.fieldGap,
  },
  // Full-width panel listing each consolidated route as a monospace pill.
  // Pills keep long route names like `il_chi_wed_pm_loop_3` on one line
  // instead of word-breaking one letter per line in a narrow column.
  '& .csm-route-panel': {
    background: theme.colors.bgMuted,
    borderRadius: theme.radii.input,
    padding: theme.spacing.bodyPadding,
    marginBottom: theme.spacing.sectionGap,
  },
  '& .csm-route-panel-title': {
    fontSize: theme.typography.microLabel.fontSize,
    fontWeight: theme.typography.microLabel.fontWeight,
    letterSpacing: theme.typography.microLabel.letterSpacing,
    color: theme.colors.textMuted,
    textTransform: 'uppercase',
    marginBottom: theme.spacing.gapMd,
  },
  '& .csm-route-pills': {
    display: 'flex',
    flexWrap: 'wrap',
    gap: theme.spacing.gapMd,
  },
  '& .csm-route-pill': {
    fontFamily: 'monospace',
    fontSize: theme.sizes.smallFontSize,
    padding: theme.spacing.pillPadding,
    background: theme.colors.bgCard,
    border: `1px solid ${theme.colors.borderLight}`,
    borderRadius: theme.radii.pill,
    color: theme.colors.textDark,
    whiteSpace: 'nowrap',
  },
  '& .csm-stat': {
    background: theme.colors.bgMuted,
    borderRadius: theme.radii.input,
    padding: theme.spacing.bodyPadding,
  },
  '& .csm-stat-label': {
    ...theme.typography.microLabel,
    color: theme.colors.textMuted,
    textTransform: 'uppercase',
    marginBottom: theme.spacing.inputLabelGap,
  },
  '& .csm-stat-value': {
    fontSize: theme.typography.statValue.fontSize,
    fontWeight: theme.fontWeights.semibold,
    color: theme.colors.textDark,
    lineHeight: theme.lineHeights.tight,
  },
  '& .csm-stat-sub': {
    marginTop: theme.spacing.inputLabelGap,
    fontSize: theme.typography.hint.fontSize,
    color: theme.colors.textMuted,
    fontFamily: 'monospace',
    wordBreak: 'break-all',
  },
  '& .csm-section': {
    marginBottom: theme.spacing.sectionGap,
  },
  '& .csm-section-title': {
    fontSize: theme.typography.body.fontSize,
    fontWeight: theme.fontWeights.semibold,
    color: theme.colors.textDark,
    marginBottom: theme.spacing.inputLabelGap,
  },
  '& .csm-section-desc': {
    fontSize: theme.sizes.smallFontSize,
    color: theme.colors.textMuted,
    marginBottom: theme.spacing.fieldGap,
  },
  '& .csm-table': {
    width: '100%',
    borderCollapse: 'collapse',
    fontSize: theme.typography.label.fontSize,
  },
  '& .csm-table th': {
    textAlign: 'left',
    padding: '8px 10px',
    background: theme.colors.bgMuted,
    fontSize: theme.typography.hint.fontSize,
    fontWeight: theme.fontWeights.semibold,
    color: theme.colors.textMuted,
    textTransform: 'uppercase',
    letterSpacing: '0.5px',
    borderBottom: `1px solid ${theme.colors.borderLight}`,
  },
  '& .csm-table td': {
    padding: '8px 10px',
    borderBottom: `1px solid ${theme.colors.borderLight}`,
    verticalAlign: 'middle',
  },
  '& .csm-table tr:last-of-type td': {
    borderBottom: 'none',
  },
  '& .csm-table-stop-name': {
    fontFamily: 'monospace',
    color: theme.colors.textDark,
    wordBreak: 'break-all',
  },
  '& .csm-table-route': {
    fontFamily: 'monospace',
    color: theme.colors.textMuted,
    fontSize: theme.sizes.smallFontSize,
    wordBreak: 'break-all',
  },
  '& .csm-empty': {
    padding: theme.spacing.bodyPadding,
    textAlign: 'center',
    color: theme.colors.textMuted,
    fontStyle: 'italic',
    background: theme.colors.bgMuted,
    borderRadius: theme.radii.input,
  },
  '& .csm-cta-send': {
    background: theme.colors.consolidate,
    color: theme.colors.white,
    border: 'none',
  },
  '& .csm-cta-send:hover:not(:disabled)': {
    background: theme.colors.consolidateHover,
  },
  '& .csm-cta-send:disabled': {
    background: theme.colors.disabled,
    cursor: 'not-allowed',
  },
});

function ConsolidateSelectedModal({
  show,
  source,
  selectedRouteGroups,
  availableDrivers,
  availableVans,
  reasonText,
  onSubmit,
  onCancel,
}) {
  const consolidatedCount = selectedRouteGroups.length;

  const stopsToRedistribute = useMemo(() => {
    const out = [];
    selectedRouteGroups.forEach((group) => {
      group.stops.forEach((stop) => {
        out.push({
          stop,
          routeName: group.routeName,
          stopKey: getStopRowKey(stop),
        });
      });
    });
    return out;
  }, [selectedRouteGroups]);

  const canSubmit = consolidatedCount > 0;

  const handleSubmit = () => {
    if (!canSubmit) return;
    onSubmit({
      selectedRouteNames: selectedRouteGroups.map((g) => g.routeName),
    });
  };

  return (
    <Modal show={show} onHide={onCancel} backdrop="static" size="lg">
      <Modal.Header closeButton>
        <Modal.Title>
          🔀 Review Route Consolidation
          <div
            style={{
              fontSize: theme.sizes.smallFontSize,
              color: theme.colors.textMuted,
              fontWeight: theme.fontWeights.normal,
              marginTop: theme.spacing.labelGap,
            }}
          >
            Day Of — sends a full reroute to NextMV
          </div>
        </Modal.Title>
      </Modal.Header>
      <Modal.Body>
        <StyledDiv data-source={source}>
          <div className="csm-warning" data-testid="csm-warning">
            <strong>This action is final.</strong> Once submitted, NextMV will
            redistribute the stops below across the remaining vehicles.
            Consolidations cannot be reverted.
          </div>

          <div className="csm-stats">
            <div className="csm-stat" data-testid="csm-stat-remaining-drivers">
              <div className="csm-stat-label">Remaining drivers</div>
              <div className="csm-stat-value">{availableDrivers}</div>
            </div>
            <div className="csm-stat" data-testid="csm-stat-remaining-vehicles">
              <div className="csm-stat-label">Remaining vans</div>
              <div className="csm-stat-value">{availableVans}</div>
            </div>
            <div
              className="csm-stat"
              data-testid="csm-stat-stops-to-redistribute"
            >
              <div className="csm-stat-label">Stops to redistribute</div>
              <div className="csm-stat-value">{stopsToRedistribute.length}</div>
            </div>
          </div>

          {selectedRouteGroups.length > 0 && (
            <div
              className="csm-route-panel"
              data-testid="csm-routes-being-consolidated"
            >
              <div className="csm-route-panel-title">
                Routes being consolidated ({consolidatedCount})
              </div>
              <div className="csm-route-pills">
                {selectedRouteGroups.map((g) => (
                  <span
                    key={g.routeName}
                    className="csm-route-pill"
                    data-testid={`csm-route-pill-${g.routeName}`}
                  >
                    {g.routeName}
                  </span>
                ))}
              </div>
            </div>
          )}

          {reasonText && (
            <div className="csm-section" data-testid="csm-reason">
              <div className="csm-section-title">Reason</div>
              <div>{reasonText}</div>
            </div>
          )}

          <div className="csm-section">
            <div className="csm-section-title">Stops to redistribute</div>
            <div className="csm-section-desc">
              NextMV will reassign these stops to the remaining vehicles and
              keep the existing delivery windows. To pin a specific stop to a
              different window, use the route editor&apos;s Pin action after
              this reroute confirms.
            </div>
            {/* Delivery window column intentionally omitted — windows aren't */}
            {/* editable here and Nextmv carries them through unchanged. */}
            {stopsToRedistribute.length === 0 ? (
              <div className="csm-empty" data-testid="csm-empty-stops">
                No routes selected. Use the checkboxes on the route cards to
                pick the route(s) without a driver / van, then re-open this
                modal.
              </div>
            ) : (
              <table className="csm-table" data-testid="csm-stops-table">
                <thead>
                  <tr>
                    <th>Stop</th>
                    <th>From route</th>
                  </tr>
                </thead>
                <tbody>
                  {stopsToRedistribute.map(({ stop, routeName, stopKey }) => (
                    <tr key={stopKey} data-testid={`csm-row-${stopKey}`}>
                      <td className="csm-table-stop-name">
                        {stop.displayName || stop.id || stop.stop || '—'}
                      </td>
                      <td className="csm-table-route">{routeName}</td>
                    </tr>
                  ))}
                </tbody>
              </table>
            )}
          </div>
        </StyledDiv>
      </Modal.Body>
      <Modal.Footer>
        <Button onClick={onCancel} data-testid="csm-cancel">
          Cancel
        </Button>
        <Button
          onClick={handleSubmit}
          disabled={!canSubmit}
          className="csm-cta-send"
          data-testid="csm-submit"
        >
          Send to NextMV for Reroute
        </Button>
      </Modal.Footer>
    </Modal>
  );
}

ConsolidateSelectedModal.propTypes = {
  show: PropTypes.bool.isRequired,
  source: PropTypes.oneOf(['headcount', 'manual']),
  selectedRouteGroups: PropTypes.arrayOf(
    PropTypes.shape({
      routeName: PropTypes.string.isRequired,
      stops: PropTypes.arrayOf(PropTypes.object).isRequired,
    }),
  ),
  // Headcount-reported counts for the day. Kept independent so a supervisor
  // who reports 50 drivers / 55 vans sees 50/55 here rather than 50/50.
  availableDrivers: PropTypes.number,
  availableVans: PropTypes.number,
  reasonText: PropTypes.string,
  onSubmit: PropTypes.func.isRequired,
  onCancel: PropTypes.func.isRequired,
};

ConsolidateSelectedModal.defaultProps = {
  source: 'manual',
  selectedRouteGroups: [],
  availableDrivers: 0,
  availableVans: 0,
  reasonText: '',
};

export default ConsolidateSelectedModal;
